import { useEffect } from "react";
import type { RefObject } from "react";

/**
 * axe `scrollable-region-focusable`: a keyboard user can only reach a
 * horizontally-scrolling box if it is itself a tab stop with a name. Wiring
 * that up on a single element the caller measures is not enough when the
 * overflow is actually absorbed by a NESTED scroll container instead — KaTeX
 * wraps display math in its own `.katex-display { overflow-x: auto }`, and a
 * GFM table could get a scroll wrapper of its own too. In that case the outer
 * box never has `scrollWidth > clientWidth`, because the inner one already
 * ate the overflow, so no fix that only looks at the outer box can work.
 *
 * This hook scans the whole subtree rooted at `rootRef.current` — the root
 * itself always included — for every element whose *computed* `overflow-x`
 * is `auto`/`scroll`, and toggles `tabindex`/`role="group"`/`aria-label` on
 * each one based on whether it is currently overflowing: `scrollWidth >
 * clientWidth + 1`, or — since that is rounded to a whole pixel and only
 * looks sideways — any descendant whose (fractional) `getBoundingClientRect()`
 * extends past the container's own on *any* edge, left/right/top/bottom, the
 * same comparison axe's check itself makes. The vertical half of that is not
 * a horizontal-scrolling concern by itself, but a tall inline glyph (an
 * integral with limits) can poke above or below its line without the box
 * ever getting wider, and axe flags that escape too — matching what it
 * checks matters more than only handling the case the class name suggests.
 * Attributes are set imperatively on the DOM rather than through React state
 * because most candidates — a nested
 * `.katex-display`, a table wrapper — are not React-rendered elements this
 * component controls; only the outer root is.
 *
 * Re-scans on mount/update, on a `ResizeObserver` over the root and every
 * candidate (inner content can change width without the clipped outer box
 * ever resizing), once web fonts finish loading (KaTeX reflows before glyphs
 * are ready, which can change what overflows), and on window resize. No-ops
 * where `ResizeObserver` does not exist (jsdom in component tests), and is
 * itself a no-op — nothing is scanned or touched — while `enabled` is false. Turning `enabled`
 * off again removes the attributes it added, so a column that stops scrolling is not a tab stop.
 */
export interface UseFocusableOverflowOptions {
  /** Skip scanning entirely, e.g. a variant that never opens a scroll box. */
  enabled: boolean;
  /** Accessible name given to every element found to be overflowing. */
  label: string;
}

function isOverflowCandidate(el: Element): boolean {
  const overflowX = getComputedStyle(el).overflowX;
  return overflowX === "auto" || overflowX === "scroll";
}

function collectCandidates(root: Element): Element[] {
  const nested = Array.from(root.querySelectorAll("*")).filter(isOverflowCandidate);
  return [root, ...nested];
}

/**
 * `scrollWidth`/`clientWidth` are rounded to whole pixels and only measure
 * the horizontal axis, so two cases slip past them: a KaTeX fraction that
 * overflows its box by less than a pixel (rounds down to "equal"), and a
 * tall inline symbol — an integral with limits, say — whose glyph is taller
 * than the surrounding text line and pokes past the box's *top* or *bottom*
 * edge without ever making it horizontally wider. axe's own
 * `scrollable-region-focusable` check does not round and does not favour
 * one axis: it compares every descendant's (fractional) bounding rect
 * against the container's on all four edges. Mirror that here so both kinds
 * of overflow the integer, horizontal-only comparison misses still get a
 * tab stop — this is about matching what axe will flag, not just what
 * visibly needs a horizontal scrollbar.
 */
function isOverflowing(el: Element): boolean {
  if (el.scrollWidth > el.clientWidth + 1) return true;
  const bounds = el.getBoundingClientRect();
  if (bounds.width === 0 && bounds.height === 0) return false;
  return Array.from(el.querySelectorAll("*")).some((child) => {
    const rect = child.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return false;
    return (
      rect.left < bounds.left - 0.5 ||
      rect.right > bounds.right + 0.5 ||
      rect.top < bounds.top - 0.5 ||
      rect.bottom > bounds.bottom + 0.5
    );
  });
}

function clearOverflowState(el: Element): void {
  el.removeAttribute("tabindex");
  el.removeAttribute("role");
  el.removeAttribute("aria-label");
}

function applyOverflowState(el: Element, label: string): void {
  const overflowing = isOverflowing(el);
  if (overflowing) {
    el.setAttribute("tabindex", "0");
    // `role="group"`, not `role="region"`: a page can open several of these
    // at once (a statement, a hint, a solution), all sharing the one generic
    // label ("Scrollable content") — as landmarks, axe `landmark-unique`
    // rightly flags same-named `region`s. `group` gives the same focusable,
    // named container without being a landmark that has to be unique.
    el.setAttribute("role", "group");
    el.setAttribute("aria-label", label);
  } else {
    clearOverflowState(el);
  }
}

export function useFocusableOverflow(
  rootRef: RefObject<HTMLElement | null>,
  /** Included in the effect's dependencies so a content change re-scans. */
  content: unknown,
  { enabled, label }: UseFocusableOverflowOptions,
): void {
  useEffect(() => {
    if (!enabled) return;
    const root = rootRef.current;
    if (!root) return;

    const candidates = collectCandidates(root);
    const scan = () => candidates.forEach((el) => applyOverflowState(el, label));

    scan();

    // jsdom (component tests) has no ResizeObserver/document.fonts. The scan
    // above still runs once on mount/content-change; it just won't re-check
    // later there — tests drive a re-scan explicitly instead.
    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(scan);
    candidates.forEach((el) => observer.observe(el));
    window.addEventListener("resize", scan);

    let cancelled = false;
    document.fonts?.ready.then(() => {
      if (!cancelled) scan();
    });

    return () => {
      cancelled = true;
      observer.disconnect();
      window.removeEventListener("resize", scan);
    };
  }, [rootRef, content, enabled, label]);

  // Switched off (the layout went below `lg`): the regions found while it was on are plain flow
  // content again and must stop being tab stops. Only a change of `enabled` gets here, not a new
  // `content`, so a focused region never loses its tab stop mid-rescan.
  useEffect(() => {
    if (!enabled) return;
    const root = rootRef.current;
    if (!root) return;
    return () => collectCandidates(root).forEach(clearOverflowState);
  }, [rootRef, enabled]);
}
